import React, { useRef, useState } from "react";

/**
 * SVG overlay rendered on top of ONE PdfViewer page. Draws every takeoff
 * item PlanSwift-style with trade-coded colours + supports drag-to-edit
 * vertices on the selected shape.
 *
 * Geometry types supported:
 *   - area       → filled polygon (20% opacity), 3px border
 *   - linear     → 4px polyline, label at midpoint
 *   - count      → 12px filled circles per instance
 *   - elevation  → "EX: ###.##" / "PR: ###.##" + crosshair
 *   - overdig    → dashed orange polygon, "Overdig: X ft"
 *   - slope      → arrow + "Slope: X%" callout
 *
 * Colour comes from the AI-attributed `trade` field via TRADE_COLORS;
 * `color` on the item itself wins if explicitly set (lets users override
 * a single shape's colour without changing trade). Legacy items without
 * either fall back to a neutral default per type.
 */
const TRADE_COLORS = {
    earthwork: "#E67E22",
    concrete: "#A3A3A3",
    asphalt: "#2C3E50",
    framing: "#27AE60",
    drywall: "#F1C40F",
    roofing: "#8E44AD",
    plumbing: "#3498DB",
    electrical: "#E74C3C",
    hvac: "#1ABC9C",
    site_utilities: "#D35400",
    general: "#000000",
};
const LEGACY_DEFAULT = {
    area: "#10B981",
    linear: "#00A3FF",
    count: "#EF4444",
    elevation: "#3498DB",
    overdig: "#E67E22",
    slope: "#000000",
};

function colorOf(item) {
    if (item.color) return item.color;
    const t = (item.trade || "").toString().toLowerCase().replace(/\s+/g, "_");
    return TRADE_COLORS[t] || LEGACY_DEFAULT[item.type] || "#00A3FF";
}

/** Convert "#RRGGBB" → "rgba(r,g,b,a)" for fill opacity. */
function hexToRgba(hex, alpha) {
    const clean = (hex || "").replace("#", "");
    if (clean.length !== 6) return `rgba(0,163,255,${alpha})`;
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

export default function TakeoffOverlay({
    items,
    selectedId,
    onSelect,
    onPointsCommit,
    pageWidth,
    pageHeight,
    unitSystem = "imperial",
}) {
    const [drag, setDrag] = useState(null); // {itemId, index, pts}
    const svgRef = useRef(null);

    if (!items || items.length === 0) return null;
    const refW = items[0].page_width || pageWidth;
    const refH = items[0].page_height || pageHeight;
    if (!refW || !refH) return null;

    const toViewBoxXY = (evt) => {
        const svg = svgRef.current;
        if (!svg) return null;
        const rect = svg.getBoundingClientRect();
        const x = ((evt.clientX - rect.left) / rect.width) * refW;
        const y = ((evt.clientY - rect.top) / rect.height) * refH;
        return [
            Math.max(0, Math.min(x, refW - 1)),
            Math.max(0, Math.min(y, refH - 1)),
        ];
    };

    const handleVertexDown = (item, idx) => (e) => {
        e.stopPropagation();
        e.preventDefault();
        setDrag({ itemId: item.id, index: idx, pts: item.points.map((p) => [...p]) });
        try { e.target.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    };
    const handleVertexMove = (e) => {
        if (!drag) return;
        const xy = toViewBoxXY(e);
        if (!xy) return;
        const next = drag.pts.map((p, i) => (i === drag.index ? [round(xy[0]), round(xy[1])] : p));
        setDrag({ ...drag, pts: next });
    };
    const handleVertexUp = (e) => {
        if (!drag) return;
        onPointsCommit && onPointsCommit(drag.itemId, drag.pts);
        setDrag(null);
        try { e.target.releasePointerCapture(e.pointerId); } catch { /* */ }
    };

    return (
        <svg
            ref={svgRef}
            data-testid="takeoff-overlay"
            viewBox={`0 0 ${refW} ${refH}`}
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ overflow: "visible" }}
            onPointerMove={handleVertexMove}
            onPointerUp={handleVertexUp}
            onPointerLeave={handleVertexUp}
        >
            <defs>
                {/* Re-usable arrowhead marker for slope/grade arrows. The
                    `userSpaceOnUse` units keep the arrow the same physical
                    size regardless of the line's length. */}
                <marker
                    id="takeoff-arrow"
                    viewBox="0 0 10 10"
                    refX="9"
                    refY="5"
                    markerWidth="10"
                    markerHeight="10"
                    orient="auto-start-reverse"
                    markerUnits="userSpaceOnUse"
                >
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
                </marker>
            </defs>
            {items.map((it) => {
                const isSelected = selectedId === it.id;
                const pts = (drag && drag.itemId === it.id) ? drag.pts : it.points;
                const itemWithLivePts = pts === it.points ? it : { ...it, points: pts };
                const common = {
                    onClick: (e) => {
                        e.stopPropagation();
                        onSelect && onSelect(it.id);
                    },
                    "data-testid": `takeoff-shape-${it.id}`,
                    "data-takeoff-id": it.id,
                    "data-takeoff-type": it.type,
                    "data-takeoff-trade": it.trade || "",
                    style: { pointerEvents: "auto", cursor: "pointer" },
                };
                return (
                    <g key={it.id}>
                        <ShapeBody item={itemWithLivePts} isSelected={isSelected} common={common} unitSystem={unitSystem} />
                        {isSelected && onPointsCommit && (
                            <VertexHandles
                                points={pts}
                                onPointerDown={(idx) => handleVertexDown(it, idx)}
                            />
                        )}
                    </g>
                );
            })}
        </svg>
    );
}

function round(n) { return Math.round(n * 10) / 10; }

function centroidOf(points) {
    if (!points || points.length === 0) return [0, 0];
    let sx = 0, sy = 0;
    for (const p of points) { sx += p[0]; sy += p[1]; }
    return [sx / points.length, sy / points.length];
}

function midpointOf(points) {
    if (!points || points.length < 2) return points && points[0] ? [...points[0]] : [0, 0];
    // Pick the geometric midpoint of the polyline by walking until we
    // pass half the total length — gives a stable label position even
    // for L-shaped runs where the centroid would land off the line.
    let total = 0;
    for (let i = 0; i < points.length - 1; i++) {
        const dx = points[i + 1][0] - points[i][0];
        const dy = points[i + 1][1] - points[i][1];
        total += Math.hypot(dx, dy);
    }
    if (total === 0) return [...points[0]];
    let walked = 0;
    for (let i = 0; i < points.length - 1; i++) {
        const dx = points[i + 1][0] - points[i][0];
        const dy = points[i + 1][1] - points[i][1];
        const seg = Math.hypot(dx, dy);
        if (walked + seg >= total / 2) {
            const t = (total / 2 - walked) / seg;
            return [points[i][0] + dx * t, points[i][1] + dy * t];
        }
        walked += seg;
    }
    return [...points[points.length - 1]];
}

function ShapeBody({ item, isSelected, common, unitSystem = "imperial" }) {
    const accent = colorOf(item);

    if (item.type === "area") {
        const d =
            item.points
                .map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`)
                .join(" ") + " Z";
        const [lx, ly] = item.points[0];
        return (
            <g {...common}>
                <path
                    d={d}
                    fill={hexToRgba(accent, 0.2)}
                    stroke={isSelected ? "#FFFFFF" : accent}
                    strokeWidth={isSelected ? 4 : 3}
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
                <TakeoffLabel x={lx} y={ly} text={labelFor(item, unitSystem)} />
            </g>
        );
    }

    if (item.type === "linear") {
        const d = item.points
            .map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`)
            .join(" ");
        const [mx, my] = midpointOf(item.points);
        return (
            <g {...common}>
                <path
                    d={d}
                    fill="none"
                    stroke={isSelected ? "#FFFFFF" : accent}
                    strokeWidth={isSelected ? 5 : 4}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
                <TakeoffLabel x={mx} y={my} text={labelFor(item, unitSystem)} />
            </g>
        );
    }

    if (item.type === "count") {
        return (
            <g {...common}>
                {item.points.map((p, i) => (
                    <circle
                        key={i}
                        cx={p[0]}
                        cy={p[1]}
                        r={6}
                        fill={accent}
                        stroke={isSelected ? "#FFFFFF" : "rgba(0,0,0,0.5)"}
                        strokeWidth={isSelected ? 2 : 1}
                        vectorEffect="non-scaling-stroke"
                    />
                ))}
                <TakeoffLabel
                    x={item.points[0][0]}
                    y={item.points[0][1] - 14}
                    text={labelFor(item, unitSystem)}
                />
            </g>
        );
    }

    if (item.type === "elevation") {
        // Existing = blue, Proposed = green (Gemini spec). Crosshair sits
        // exactly at the callout point so the label reads next to it.
        const [px, py] = item.points[0];
        const isProposed = (item.kind || "EX").toUpperCase() === "PR";
        const elColor = isProposed ? "#27AE60" : "#3498DB";
        const v = item.value;
        const text = `${isProposed ? "PR" : "EX"}: ${v != null ? Number(v).toFixed(2) : "?"}`;
        return (
            <g {...common}>
                {/* Crosshair: a cross of two short lines */}
                <line x1={px - 8} y1={py} x2={px + 8} y2={py} stroke={elColor} strokeWidth={2} vectorEffect="non-scaling-stroke" />
                <line x1={px} y1={py - 8} x2={px} y2={py + 8} stroke={elColor} strokeWidth={2} vectorEffect="non-scaling-stroke" />
                {/* Hover/click target so the user can select it */}
                <circle cx={px} cy={py} r={12} fill="transparent" stroke={isSelected ? "#FFFFFF" : "transparent"} strokeWidth={2} vectorEffect="non-scaling-stroke" />
                <TakeoffLabel x={px + 10} y={py - 10} text={text} fill={elColor} />
            </g>
        );
    }

    if (item.type === "overdig") {
        // Dashed orange polygon. Depth shown at centroid.
        const d =
            item.points
                .map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`)
                .join(" ") + " Z";
        const [cx, cy] = centroidOf(item.points);
        const orange = "#E67E22";
        const depthText = item.depth_ft != null ? `Overdig: ${formatNumber(item.depth_ft)} ft` : "Overdig";
        return (
            <g {...common}>
                <path
                    d={d}
                    fill={hexToRgba(orange, 0.12)}
                    stroke={isSelected ? "#FFFFFF" : orange}
                    strokeWidth={isSelected ? 5 : 4}
                    strokeLinejoin="round"
                    strokeDasharray="14 8"
                    vectorEffect="non-scaling-stroke"
                />
                <TakeoffLabel x={cx} y={cy} text={depthText} fill={orange} />
            </g>
        );
    }

    if (item.type === "slope") {
        // 2-point arrow pointing down-grade. Marker is rendered via
        // `marker-end` so it auto-orients with the line direction.
        const [a, b] = item.points;
        const [mx, my] = midpointOf(item.points);
        const slopeText = item.slope_pct != null ? `Slope: ${formatNumber(item.slope_pct)}%` : "Slope";
        return (
            <g {...common} style={{ ...common.style, color: isSelected ? "#FFFFFF" : "#000000" }}>
                <line
                    x1={a[0]}
                    y1={a[1]}
                    x2={b[0]}
                    y2={b[1]}
                    stroke="currentColor"
                    strokeWidth={isSelected ? 4 : 3}
                    strokeLinecap="round"
                    markerEnd="url(#takeoff-arrow)"
                    vectorEffect="non-scaling-stroke"
                />
                <TakeoffLabel x={mx} y={my} text={slopeText} fill={isSelected ? "#FFFFFF" : "#000000"} />
            </g>
        );
    }

    // Fallback for unknown types — just outline the points so they don't
    // disappear from the UI silently.
    return (
        <g {...common}>
            {item.points.map((p, i) => (
                <circle key={i} cx={p[0]} cy={p[1]} r={4} fill={accent} vectorEffect="non-scaling-stroke" />
            ))}
        </g>
    );
}

function VertexHandles({ points, onPointerDown }) {
    return (
        <g data-testid="takeoff-vertex-handles">
            {points.map((p, i) => (
                <rect
                    key={i}
                    x={p[0] - 9}
                    y={p[1] - 9}
                    width={18}
                    height={18}
                    fill="#FFFFFF"
                    stroke="#0A1128"
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: "auto", cursor: "grab", touchAction: "none" }}
                    onPointerDown={onPointerDown(i)}
                    data-testid={`takeoff-vertex-${i}`}
                />
            ))}
        </g>
    );
}

function labelFor(it, unitSystem = "imperial") {
    const metric = unitSystem === "metric";
    const areaUnit = metric ? "m\u00B2" : "sf";
    const linearUnit = metric ? "m" : "lf";
    const q =
        (it.type === "area" || it.type === "overdig") && it.quantity_sf != null
            ? `${formatNumber(it.quantity_sf)} ${areaUnit}`
            : (it.type === "linear" || it.type === "slope") && it.quantity_lf != null
              ? `${formatNumber(it.quantity_lf)} ${linearUnit}`
              : it.type === "count" && it.quantity_each != null
                ? `${formatNumber(it.quantity_each)}`
                : "";
    return q ? `${it.label} · ${q}` : it.label;
}

function formatNumber(n) {
    const num = Number(n);
    if (!Number.isFinite(num)) return String(n);
    return Number.isInteger(num) ? num.toString() : num.toFixed(1);
}

/**
 * 14px bold white label with a thick black stroke outline (paint-order
 * tricks the stroke under the fill so the outline reads at any zoom).
 * Per Gemini spec: horizontal, white-on-black-outline, readable on any
 * background.
 */
function TakeoffLabel({ x, y, text, fill = "#FFFFFF" }) {
    return (
        <g style={{ pointerEvents: "none" }}>
            <text
                x={x + 8}
                y={y - 8}
                fontSize={14}
                fontWeight="700"
                fill={fill}
                stroke="#000000"
                strokeWidth={3}
                paintOrder="stroke"
                style={{ userSelect: "none", fontFamily: "ui-sans-serif, system-ui, sans-serif" }}
            >
                {text}
            </text>
        </g>
    );
}
